import React, { useState } from 'react';
import { TrendingUp, Download, RefreshCw, AlertCircle, Filter } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';
import { ExportButtons } from './ExportButtons';
import { csvCell } from '../utils/csvSafe';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TonnageRow {
  period_year: string;
  period_month: string;
  period_month_name: string;
  material_group: string;
  total_tons: string;
  company_tons: string;
  independent_tons: string;
  facility_summary_tons: string;
}

interface Filters {
  preset: string;
  material_group: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MATERIAL_GROUPS = ['Disposal', 'Recycle', 'Reuse', 'Land Application'];

const GROUP_COLORS: Record<string, string> = {
  'Disposal':         '#dc2626',
  'Recycle':          '#16a34a',
  'Reuse':            '#2563eb',
  'Land Application': '#9333ea',
};

const PRESETS = [
  { value: 'trailing12', label: 'Trailing 12 Months' },
  { value: 'ytd',        label: 'Year to Date' },
  { value: 'trailing6',  label: 'Trailing 6 Months' },
  { value: 'trailing3',  label: 'Trailing 3 Months' },
];

function toNum(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function computeRange(preset: string): { startYear: number; startMonth: number; endYear: number; endMonth: number } {
  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1;

  let startYear = endYear;
  let startMonth = endMonth;

  const back = (months: number) => {
    let m = endMonth - months;
    let y = endYear;
    while (m <= 0) { m += 12; y -= 1; }
    return { y, m };
  };

  if (preset === 'ytd') {
    startYear = endYear;
    startMonth = 1;
  } else if (preset === 'trailing6') {
    const r = back(5); startYear = r.y; startMonth = r.m;
  } else if (preset === 'trailing3') {
    const r = back(2); startYear = r.y; startMonth = r.m;
  } else {
    const r = back(11); startYear = r.y; startMonth = r.m;
  }

  return { startYear, startMonth, endYear, endMonth };
}

function exportCSV(data: TonnageRow[]) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => csvCell((r as any)[h])).join(','));
  const csv = [headers.map(csvCell).join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'regional-tonnage-trend.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RegionalTonnage() {
  const { getToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [filters, setFilters] = useState<Filters>({ preset: 'trailing12', material_group: '' });
  const [data, setData] = useState<TonnageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const range = computeRange(filters.preset);
      const params: Record<string, string> = {
        start_year:  String(range.startYear),
        start_month: String(range.startMonth),
        end_year:    String(range.endYear),
        end_month:   String(range.endMonth),
      };
      if (filters.material_group) params.material_group = filters.material_group;

      const res = await fetch(`${API_URL}/reports/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_id: 'dashboard.regional_tonnage', params }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result.data ?? []);
      setHasLoaded(true);
    } catch (e: any) {
      setError(e.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  // Reshape long-form data into one row per period, one column per material group
  const chartData = React.useMemo(() => {
    const byPeriod: Record<string, any> = {};
    data.forEach(row => {
      const key = `${row.period_year}-${String(row.period_month).padStart(2, '0')}`;
      if (!byPeriod[key]) {
        byPeriod[key] = { period: `${row.period_month_name} ${row.period_year}`, sortKey: key };
      }
      byPeriod[key][row.material_group] = toNum(row.total_tons);
    });
    return Object.values(byPeriod).sort((a: any, b: any) => a.sortKey.localeCompare(b.sortKey));
  }, [data]);

  const groupsPresent = React.useMemo(
    () => Array.from(new Set(data.map(r => r.material_group))).filter(Boolean),
    [data]
  );

  const totalTons = data.reduce((s, r) => s + toNum(r.total_tons), 0);
  const totalCompany = data.reduce((s, r) => s + toNum(r.company_tons), 0);
  const totalIndependent = data.reduce((s, r) => s + toNum(r.independent_tons), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-emerald-600 rounded-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Regional Tonnage</h1>
              <p className="text-sm text-gray-500">Company-wide tonnage trend by material group</p>
            </div>
          </div>
					{hasLoaded && data.length > 0 && (
					  <div className="flex items-center space-x-2">
					    <button onClick={() => exportCSV(data)}
					      className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors">
					      <Download className="w-4 h-4" /><span>Export CSV</span>
					    </button>
					    <ExportButtons
					      reportTitle="Regional Tonnage"
					      subtitle={PRESETS.find(p => p.value === filters.preset)?.label ?? ''}
					     getExportData={() => ({ columns: Object.keys(data[0]), data: data as unknown as Record<string, string>[] })}
					    />
					  </div>
					)}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Filters</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
              <select value={filters.preset} onChange={e => setFilters(f => ({ ...f, preset: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Material Group</label>
              <select value={filters.material_group} onChange={e => setFilters(f => ({ ...f, material_group: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                <option value="">All Groups</option>
                {MATERIAL_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={fetchReport} disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run Report'}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Failed to load report</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Summary cards */}
        {hasLoaded && data.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Tons',       value: totalTons.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-emerald-600' },
              { label: 'Company Tons',     value: totalCompany.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-blue-600' },
              { label: 'Independent Tons', value: totalIndependent.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: 'text-purple-600' },
            ].map(card => (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Trend chart */}
        {hasLoaded && chartData.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toLocaleString() : v)} />
                <Legend verticalAlign="top" align="center" wrapperStyle={{ fontSize: 12, paddingBottom: 12 }} />
                {groupsPresent.map(g => (
                  <Bar key={g} dataKey={g} stackId="a" fill={GROUP_COLORS[g] ?? '#6b7280'} radius={[0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Table */}
        {hasLoaded && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {data.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-25" />
                <p className="text-sm font-medium">No data found for the selected range.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {['Period', 'Material Group', 'Total Tons', 'Company Tons', 'Independent Tons', 'Facility Summary Tons'].map(col => (
                        <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{row.period_month_name} {row.period_year}</td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.material_group}</td>
                        <td className="px-4 py-3 text-right text-gray-800 tabular-nums font-medium">{toNum(row.total_tons).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right text-gray-600 tabular-nums">{toNum(row.company_tons).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right text-gray-600 tabular-nums">{toNum(row.independent_tons).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-right text-gray-500 tabular-nums">{toNum(row.facility_summary_tons).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!hasLoaded && !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-20">
            <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-base font-medium text-gray-500">Select a date range and click Run Report</p>
          </div>
        )}
      </main>
    </div>
  );
}
